import React from 'react'
import { CircularLoader } from '@st-operations/uikit/src/v2'
import format from '@st-operations/uikit/lib/format.js'

import { useLazyLoad } from '_hooks'
import useEventsChartDataConverter from '../../hooks/useEventsChartDataConverter'
import twoNonZeroDecimals from '_lib/twoNonZeroDecimals'

import './styles.scss'
import { Legend } from 'recharts'

const LINE_COLORS = [
  '#5BAC4D',
  '#FF2710',
  '#7DE2D3',
  '#0CB2B2',
  '#146A59',
  '#91D400',
  '#B581A2',
  '#34158C',
  '#A40965',
  '#F7DB71',
  '#13682B',
  '#7D69B5',
]

const LineUserCountsChart = ({
  loading,
  data = [],
  hoveredKeyword,
  emptyMessage = "There's no data to show",
  onHoveredKeywordChange = () => {},
  transformKeyword = (kw) => kw,
}) => {
  const normalizedData = useEventsChartDataConverter(data, (data) => data)

  const customTooltipOnYourLine = (e) => {
    if (e.active && e.payload?.[0]) {
      const val = e.payload[0].payload[hoveredKeyword]
      const absVal = Math.abs(val, 10)
      return (
        <If condition={hoveredKeyword}>
          <div className="line-trends-chart__tooltip">
            <div>{e.payload[0].payload['date'].toLocaleDateString()}</div>
            <div>
              {transformKeyword(hoveredKeyword)}:{' '}
              {`${
                (Math.sign(val) < 0 ? '-' : '') +
                (absVal < 1000 ? twoNonZeroDecimals(absVal) : format('metric', absVal))
              }`}
            </div>
          </div>
        </If>
      )
    }
    return null
  }

  const [
    { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis },
    loadingRecharts,
  ] = useLazyLoad(import('recharts'))

  return (
    <section className="line-trends-chart__chart">
      <If condition={!loading && !loadingRecharts}>
        <If condition={normalizedData.length > 0}>
          <ResponsiveContainer width="99%">
            <LineChart
              width={500}
              height={300}
              data={normalizedData}
              margin={{
                top: 5,
                right: 30,
                left: 20,
                bottom: 50,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={(val) => val.toLocaleDateString()}
                angle={-70}
                textAnchor="end"
                height={80}
              />
              <YAxis
                tickFormatter={(val) => {
                  const absVal = Math.abs(val, 10)
                  return `${
                    (Math.sign(val) < 0 ? '-' : '') +
                    (absVal < 1000 ? twoNonZeroDecimals(absVal) : format('metric', absVal))
                  }`
                }}
                label={{
                  value: 'User Count',
                  angle: -90,
                  position: 'insideLeft',
                  offset: 0,
                  style: { textAnchor: 'middle' },
                }}
                width={70}
              />
              <For
                each="lineName"
                index="index"
                of={Object.keys(data || {}).sort((k1) => (k1 === hoveredKeyword ? 1 : -1))}
              >
                <Line
                  type="monotone"
                  dataKey={lineName}
                  stroke={
                    // (disabledKeywords.includes(lineName) && hoveredKeyword !== lineName) ||
                    hoveredKeyword && hoveredKeyword !== lineName
                      ? '#eee'
                      : LINE_COLORS[Object.keys(data || {}).findIndex((f) => f === lineName)] ||
                        'black'
                  }
                  strokeWidth={2}
                  dot={false}
                  activeDot={false}
                  isAnimationActive={false}
                  onMouseEnter={() => onHoveredKeywordChange(lineName)}
                  onMouseLeave={() => onHoveredKeywordChange(undefined)}
                  style={{ cursor: 'pointer' }}
                />
              </For>
              <Tooltip content={customTooltipOnYourLine} isAnimationActive={false} />
              <Legend formatter={transformKeyword} />
            </LineChart>
          </ResponsiveContainer>
        </If>
        <If condition={normalizedData.length === 0}>{emptyMessage}</If>
      </If>
      <If condition={loading}>
        <CircularLoader className="line-trends-chart__loading" />
      </If>
    </section>
  )
}

export default LineUserCountsChart
